// Tutoriales in-app (#1013) — overlay de un paso: resalta el elemento objetivo
// (si existe) y muestra la tarjeta con el texto, la captura, "No volver a
// mostrar" y "Ver en la wiki". Sin dependencias: posicionamiento propio.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useIsMobile } from "../hooks/useIsMobile";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useDarkMode } from "../context/ThemeContext";
import { wikiRoute } from "./eligibility";
import { useTutorialsStore } from "./store";
import { assetUrl, isEditableTarget, langOf, previewText } from "./utils";
import { CARD_W, cardPosition, type Rect } from "./position";

const PAD = 8; // aire alrededor del elemento resaltado
const FIND_TIMEOUT_MS = 1500;

export default function TutorialOverlay() {
  const { i18n } = useTranslation();
  const lang = langOf(i18n.language);
  const tr = (es: string, en: string) => (lang === "en" ? en : es);
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate();

  const active = useTutorialsStore((s) => s.active);
  const next = useTutorialsStore((s) => s.next);
  const prev = useTutorialsStore((s) => s.prev);
  const close = useTutorialsStore((s) => s.close);

  const [rect, setRect] = useState<Rect | null>(null);
  const [dontShow, setDontShow] = useState(false);
  // Texto del paso recortado a 100 caracteres hasta que se pide «Ver más».
  const [expanded, setExpanded] = useState(false);
  const [cardH, setCardH] = useState(320);
  const isMobile = useIsMobile("sm");
  const cardRef = useRef<HTMLDivElement>(null);

  const step = active ? active.tutorial.steps[active.step] : null;
  const target = step?.target;

  // Casilla desmarcada al abrir cada tutorial.
  useEffect(() => setDontShow(false), [active?.tutorial.id]);

  // Cada paso empieza recortado.
  useEffect(() => setExpanded(false), [active?.tutorial.id, active?.step]);

  // Busca el elemento (puede tardar en renderizarse) y sigue su posición.
  useEffect(() => {
    setRect(null);
    if (!target) return;
    let el: Element | null = null;
    let raf = 0;
    const started = Date.now();

    const measure = () => {
      if (!el || !document.contains(el)) el = document.querySelector(target);
      if (!el) {
        if (Date.now() - started < FIND_TIMEOUT_MS) raf = requestAnimationFrame(measure);
        return;
      }
      const r = el.getBoundingClientRect();
      // Oculto (display:none, colapsado): se trata como ausente → tarjeta centrada.
      if (r.width === 0 && r.height === 0) {
        setRect(null);
        return;
      }
      setRect({ top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 });
    };

    const first = () => {
      el = document.querySelector(target);
      if (el) el.scrollIntoView({ block: "nearest", inline: "nearest" });
      measure();
    };
    first();
    const onChange = () => measure();
    window.addEventListener("resize", onChange);
    window.addEventListener("scroll", onChange, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onChange);
      window.removeEventListener("scroll", onChange, true);
    };
  }, [target, active?.step]);

  useLayoutEffect(() => {
    if (cardRef.current) setCardH(cardRef.current.offsetHeight);
  });

  // La altura cambia sin re-render (la captura carga después, cambia el
  // tamaño de la ventana): se vuelve a medir para no salirse de la pantalla.
  useEffect(() => {
    const el = cardRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setCardH(el.offsetHeight));
    ro.observe(el);
    const onResize = () => setCardH(el.offsetHeight);
    window.addEventListener("resize", onResize);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", onResize);
    };
  }, [active?.tutorial.id, active?.step]);

  // Foco en la tarjeta al cambiar de paso (lectores de pantalla y teclado).
  useEffect(() => {
    cardRef.current?.focus();
  }, [active?.tutorial.id, active?.step]);

  const finish = useCallback(
    (completed: boolean) => close({ dontShowAgain: dontShow, completed }),
    [close, dontShow],
  );

  const isLastStep = active ? active.step === active.tutorial.steps.length - 1 : false;

  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        finish(false);
        return;
      }
      if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
      // Las flechas son del campo donde se escribe (cursor, opciones de un
      // select), no del tutorial.
      if (isEditableTarget(e.target)) return;
      if (e.key === "ArrowLeft") prev();
      // En el último paso, → equivale a "¡Listo!": lo marca como terminado y
      // respeta "No volver a mostrar" (antes cerraba ignorando la casilla).
      else if (isLastStep) finish(true);
      else next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, isLastStep, finish, next, prev]);

  if (!active || !step) return null;

  const { tutorial } = active;
  const total = tutorial.steps.length;
  const isLast = active.step === total - 1;
  const wiki = wikiRoute(tutorial.wiki);
  const placement = step.placement || "auto";
  const pos = isMobile ? null : cardPosition(rect, placement, cardH);
  const fullBody = step.body[lang];
  const preview = previewText(fullBody);

  const card = isDarkMode ? "bg-dark-card text-white border-dark-border" : "bg-white text-gray-900 border-gray-200";
  const muted = isDarkMode ? "text-gray-300" : "text-gray-600";
  const subtle = isDarkMode ? "text-gray-400" : "text-gray-500";

  const openWiki = () => {
    if (!wiki) return;
    finish(false);
    navigate(wiki);
  };

  return (
    <div className="fixed inset-0 z-[10000]" aria-live="polite">
      {/* Fondo: con objetivo, un "hueco" con sombra; sin él, velo completo. */}
      {rect ? (
        <div
          className="pointer-events-none fixed rounded-xl ring-2 ring-primary-400 transition-all duration-200"
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
          }}
        />
      ) : (
        <div className="fixed inset-0 bg-black/55" />
      )}
      {/* Captura los clics fuera de la tarjeta: el tutorial no se cierra por accidente. */}
      <div className="fixed inset-0" onClick={(e) => e.stopPropagation()} />

      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tutorial-step-title"
        tabIndex={-1}
        className={`fixed flex flex-col overflow-hidden border shadow-2xl outline-none ${card} ${
          isMobile ? "inset-x-0 bottom-0 max-h-[85vh] rounded-t-2xl p-5" : "rounded-2xl p-5"
        }`}
        style={
          isMobile
            ? undefined
            : { top: pos!.top, left: pos!.left, width: CARD_W, maxHeight: "min(85vh, calc(100dvh - 32px))" }
        }
      >
        <div className="mb-2 flex shrink-0 items-center justify-between gap-3">
          <span className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-wide ${subtle}`}>
            <i className={`${tutorial.icon} text-primary-500 text-base`} aria-hidden="true" />
            {tutorial.title[lang]}
          </span>
          <button
            type="button"
            onClick={() => finish(false)}
            className={`rounded-lg p-1 ${subtle} hover:text-primary-500`}
            aria-label={tr("Cerrar tutorial", "Close tutorial")}
          >
            <i className="ri-close-line text-xl" aria-hidden="true" />
          </button>
        </div>

        {/* Sólo el contenido hace scroll: el título, el texto y la captura no
            se salen de la tarjeta ni se enciman con los botones. */}
        <div
          data-testid="tutorial-card-body"
          className={`-mr-2 min-h-0 flex-1 overflow-y-auto overscroll-contain pr-2 [scrollbar-width:thin] ${
            isDarkMode ? "[color-scheme:dark]" : "[color-scheme:light]"
          }`}
        >
        <h2 id="tutorial-step-title" className="break-words text-lg font-bold leading-snug">
          {step.title[lang]}
        </h2>
        <p
          id="tutorial-step-body"
          data-testid="tutorial-step-body"
          className={`mt-2 whitespace-pre-line break-words text-sm leading-relaxed ${muted}`}
        >
          {expanded || !preview.truncated ? fullBody : preview.text}
        </p>
        {preview.truncated && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-controls="tutorial-step-body"
            className="mt-1 text-sm font-medium text-primary-500 hover:underline"
          >
            {expanded ? tr("Ver menos", "Show less") : tr("Ver más", "Show more")}
          </button>
        )}

        {step.image && (
          <img
            src={assetUrl(step.image.src)}
            alt={step.image.alt[lang]}
            className={`mt-3 max-h-[40vh] w-full rounded-lg border object-contain ${isDarkMode ? "border-dark-border" : "border-gray-200"}`}
            loading="lazy"
            onLoad={() => cardRef.current && setCardH(cardRef.current.offsetHeight)}
          />
        )}

        {wiki && (
          <button
            type="button"
            onClick={openWiki}
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary-500 hover:underline"
          >
            <i className="ri-book-open-line" aria-hidden="true" />
            {tr("Ver la guía completa en la wiki", "See the full guide in the wiki")}
          </button>
        )}
        </div>

        {/* Avance */}
        <div className="mt-4 flex shrink-0 items-center gap-1.5" aria-hidden="true">
          {tutorial.steps.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === active.step ? "w-5 bg-primary-500" : `w-1.5 ${isDarkMode ? "bg-gray-600" : "bg-gray-300"}`
              }`}
            />
          ))}
          <span className={`ml-auto text-xs ${subtle}`}>
            {tr(`Paso ${active.step + 1} de ${total}`, `Step ${active.step + 1} of ${total}`)}
          </span>
        </div>

        <div className="mt-4 flex shrink-0 flex-wrap items-center justify-between gap-3">
          <label className={`flex cursor-pointer items-center gap-2 text-sm ${muted}`}>
            <input
              type="checkbox"
              checked={dontShow}
              onChange={(e) => setDontShow(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
            />
            {tr("No volver a mostrar", "Don't show again")}
          </label>
          <div className="flex items-center gap-2">
            {active.step > 0 ? (
              <button
                type="button"
                onClick={prev}
                className={`rounded-lg px-3 py-2 text-sm font-medium ${
                  isDarkMode ? "hover:bg-dark-hover" : "hover:bg-gray-100"
                }`}
              >
                {tr("Anterior", "Back")}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => finish(false)}
                className={`rounded-lg px-3 py-2 text-sm font-medium ${subtle} ${
                  isDarkMode ? "hover:bg-dark-hover" : "hover:bg-gray-100"
                }`}
              >
                {tr("Saltar", "Skip")}
              </button>
            )}
            <button
              type="button"
              onClick={() => (isLast ? finish(true) : next())}
              className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600"
            >
              {isLast ? tr("¡Listo!", "Done!") : tr("Siguiente", "Next")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
